/**
 * src/lib/data/types.ts — Phase 5 identity + market types.
 *
 * Phase 5.5b — model revised. Three LEVELS drive ownership + view
 * structure; LABEL is a free-form display title; isAdmin is an
 * independent capability that any user can carry. Anyone past the
 * view-password gate can READ everything (§1.4). The level + isAdmin
 * pair determines what they can WRITE (§1.5).
 *
 *   level: AE   → owns own per-month forecast cells
 *   level: MID  → owns the market forecast for `marketId` (gtmTarget,
 *                 partnerMrr, churnAbs, scenarios). e.g., GTM Lead,
 *                 Sales Lead — anyone overseeing a chunk of AEs.
 *   level: EXEC → owns nothing operational. Observer / executive
 *                 oversight. e.g., CCO, Founder. Future phase adds a
 *                 company-level scenario lane that EXEC owns.
 *   isAdmin     → User CRUD + password rotation. Layers on top of any
 *                 level. Marco today: level: MID, isAdmin: true.
 */

// ─── Level enum ─────────────────────────────────────────────────────────────
export type Level = "AE" | "MID" | "EXEC";
export const LEVELS: readonly Level[] = ["AE", "MID", "EXEC"] as const;

/** Human-readable label for a level (for UI). Levels are not labels. */
export const LEVEL_LABELS_EN: Record<Level, string> = {
  AE: "AE",
  MID: "Middle management",
  EXEC: "Executive",
};
export const LEVEL_LABELS_IT: Record<Level, string> = {
  AE: "AE",
  MID: "Middle management",
  EXEC: "Esecutivo",
};

// ─── User ───────────────────────────────────────────────────────────────────
export interface User {
  /** Internal stable id, format `u_<base32>`. Generated server-side. */
  id: string;
  /** Display name. Editable by the user themselves + admins. */
  name: string;
  /** Unique. Lowercased on write. Login key once a real auth provider lands. */
  email: string;

  /** Structural level — drives ownership scope (§1.5). */
  level: Level;
  /**
   * Free-form display title shown in UI ("CCO", "Founder", "GTM Lead",
   * "Sales Lead", "AE 1", etc.). Max 50 chars. Does NOT affect
   * permissions — purely cosmetic.
   */
  label: string;

  /**
   * Markets this user operates in. Multi-value so EXECs (who oversee
   * everything) can span many countries; AEs/MIDs typically have one.
   * Each entry is a lowercase slug ("italy", "dach", "global", etc.).
   * Phase 5.5c+: future sub-step pulls the catalog from HubSpot's
   * country_of_origin enum. For now: free-form slugs the admin types.
   */
  marketIds: string[];

  /** Stringified HubSpot owner id. Optional — EXEC / admin may not have one. */
  hubspotOwnerId?: string;

  /** Opaque pointer to an external comp plan. We do NOT model plan terms. */
  commissionPlanRef?: string;

  /**
   * Admin capability — independent of level. Admins can manage User
   * records and rotate passwords. NOT visible to non-admins on the
   * picker / table (Marco's call).
   */
  isAdmin: boolean;

  /** Soft-delete via active=false. Inactive users are still referenced by historic data. */
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Shape sent to the worker when creating a new User. id + timestamps are server-assigned. */
export type UserCreate = Omit<User, "id" | "createdAt" | "updatedAt">;

/** Shape sent when updating a User. id is the route param; timestamps are server-set. */
export type UserUpdate = Partial<Omit<User, "id" | "createdAt" | "updatedAt">>;

// ─── Legacy compat — Phase 5.5b transition ──────────────────────────────────
// Older records (5.1–5.5a) used `role: "AE" | "GTM_LEAD" | "CCO" | "FOUNDER" |
// "ADMIN"` as a single field. The worker shims these to {level, label,
// isAdmin} on read so the frontend never sees the legacy shape. The
// mapping:
//   AE       → { level: "AE",   label: "AE",        isAdmin: false }
//   GTM_LEAD → { level: "MID",  label: "GTM Lead",  isAdmin: false }
//   CCO      → { level: "EXEC", label: "CCO",       isAdmin: false }
//   FOUNDER  → { level: "EXEC", label: "Founder",   isAdmin: false }
//   ADMIN    → { level: "MID",  label: "Admin",     isAdmin: true  }
// Marco's account (currently role: "ADMIN") becomes MID + label="Admin" +
// isAdmin. He can edit his label to "GTM Lead" via /admin/users anytime.

// ─── Market ─────────────────────────────────────────────────────────────────
export interface Market {
  id: string;
  name: string;
  currency: "EUR" | "USD" | "GBP";
  fiscalYearStart: string;
  fiscalYearEnd: string;
  monthlyTargetPerAe: number;
  mrrConversionRate: number;
  active: boolean;
}

// ─── Audit log (Phase 5.7) ──────────────────────────────────────────────────
export interface AuditEntry {
  ts: string;
  userId: string;
  scope: string;
  fieldsChanged: string[];
  beforeHash: string;
  afterHash: string;
}
